import React, { useState, useEffect } from 'react';
import { Trash2, RotateCcw, Shield, Download, X } from 'lucide-react';
import { Board } from '../types';
import { BoardStorage } from '../utils/storage';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

interface RecoveryUtilityProps {
    isOpen: boolean;
    onClose: () => void;
}

const RecoveryUtility: React.FC<RecoveryUtilityProps> = ({ isOpen, onClose }) => {
    const [backups, setBackups] = useState<{ id: string; board: Board; key: string }[]>([]);
    const storage = BoardStorage.getInstance();

    useEffect(() => {
        if (isOpen) {
            loadBackups();
        }
    }, [isOpen]);

    const loadBackups = () => {
        const found: { id: string; board: Board; key: string; isSessionStart?: boolean }[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('fibre_backup_') || key?.startsWith('fibre_session_start_')) {
                try {
                    const data = localStorage.getItem(key);
                    if (data) {
                        const board = JSON.parse(data) as Board;
                        found.push({
                            id: key.replace('fibre_backup_', '').replace('fibre_session_start_', ''),
                            board,
                            key,
                            isSessionStart: key.startsWith('fibre_session_start_')
                        });
                    }
                } catch (e) {
                    console.error('Failed to parse backup', key, e);
                }
            }
        }
        // Sort by updatedAt desc
        setBackups(found.sort((a, b) => b.board.updatedAt - a.board.updatedAt));
    };

    const handleRestore = async (backup: Board) => {
        if (window.confirm(`Are you sure you want to restore "${backup.title}"? This will overwrite the current cloud version.`)) {
            try {
                await storage.saveBoard(backup, authService.getCurrentUser()?.id || null, true); // force save
                toast.success('Investigation restored successfully');
                loadBackups();
            } catch (error) {
                console.error('Restore failed:', error);
                toast.error('Failed to restore investigation');
            }
        }
    };

    const handleDeleteBackup = (key: string) => {
        if (window.confirm('Are you sure you want to delete this local backup?')) {
            localStorage.removeItem(key);
            loadBackups();
            toast.success('Backup removed');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                        <Shield className="text-blue-600" size={24} />
                        Rescue Center
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                            <strong>Note:</strong> These are local snapshots saved in your browser. They are useful if a board was accidentally wiped during a session. Restoring will overwrite the current database version.
                        </p>
                    </div>

                    {backups.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <RotateCcw size={48} className="mx-auto mb-4 opacity-20" />
                            <p>No local backups found on this device.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {backups.map((item) => (
                                <div key={item.key} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
                                            {item.board.title}
                                            {item.isSessionStart && (
                                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black rounded uppercase">Session Start</span>
                                            )}
                                        </h3>
                                        <div className="flex gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            <span>Nodes: {item.board.nodes.length}</span>
                                            <span>Edges: {item.board.edges.length}</span>
                                            <span>Saved: {new Date(item.board.updatedAt).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleRestore(item.board)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                                            title="Restore to Cloud"
                                        >
                                            <Download size={14} />
                                            Restore
                                        </button>
                                        <button
                                            onClick={() => handleDeleteBackup(item.key)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                                            title="Delete Backup"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 text-center">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                        Close Registry
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecoveryUtility;
